import { defineStore } from 'pinia'
import { useNetworkStore } from '@/stores/network/store'
import * as Api from './api'
import {
  callWithMeta,
  createMeta,
  getMetaInitial,
  waitFor,
  type Meta,
} from '@/utils/store'
import { useCoreStore } from '../core/store'

type Backup = {
  id: string
  edition: string
  maxLines: number
}

type State = {
  backups: Backup[]
  meta: Meta<{
    backups: undefined
    load: undefined
    create: undefined
    restore: undefined
  }>
}

const getDefaultState = (): State => ({
  backups: [],
  meta: createMeta(['backups', 'load', 'create', 'restore']),
})

export const useBackupsStore = defineStore('backups', {
  state: getDefaultState,

  getters: {
    list(): Backup[] {
      return this.allIds.map((id) => this.find(id))
    },
    find(): (id: string) => Backup {
      return (id: string) => this.byId[id]
    },
    get: (state) => state.backups,
    byId: (state) =>
      state.backups.reduce(
        (byId, backup) => {
          byId[backup.id] = backup

          return byId
        },
        {} as Record<Backup['id'], Backup>
      ),
    allIds: (state) => Array.from(new Set(state.backups.map((b) => b.id))),
  },

  actions: {
    RESET() {
      this.backups = []
      this.meta.backups = getMetaInitial()
    },

    async load({ useCache = true } = {}) {
      const hasCached = ['pending', 'success'].includes(
        this.meta.backups.status
      )

      if (useCache && hasCached) {
        await waitFor(() => this.meta.backups.status !== 'pending')

        return
      }

      await callWithMeta({
        store: this,
        metaFields: { backups: undefined, load: undefined },
        apiCall: async () => await Api.loadBackups(),
        onSuccess: ({ result, isLatest }) => {
          if (isLatest('backups')) {
            this.backups = result.payload
          }
        },
        onError: ({ result, isLatest }) => {
          if (isLatest('load')) {
            useNetworkStore().HANDLE_LOADING_ERROR(result.payload)
          }
        },
      })
    },

    async create() {
      const { result } = await callWithMeta({
        store: this,
        metaFields: { backups: undefined, create: undefined },
        apiCall: async () => await Api.createBackup(),
        onSuccess: ({ isLatest }) => {
          if (isLatest('create')) {
            this.load({ useCache: false })
          }
        },
      })

      if (result.status === 'error') {
        throw result.payload
      }
    },

    async restore(id: string) {
      const { result } = await callWithMeta({
        store: this,
        metaFields: { backups: undefined, restore: undefined },
        apiCall: async () => await Api.restoreBackup(id),
        onSuccess: ({ isLatest }) => {
          if (isLatest('restore')) {
            useCoreStore().reloadAll()
          }
        },
      })

      if (result.status === 'error') {
        throw result.payload
      }
    },
  },
})
